import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Heading, useResponsive } from '@common-fe/common-fe';

import { DismissedBannerKey } from '@/common/constants';
import ROUTES from '@/common/routes';
import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import { IAccountDtoAccountTypeEnum } from '@/dtos';
import { useBreadcrumbs } from '@/modules/core/hooks';
import { useOrganizationProperties } from '@/modules/transaction/hooks';
import useDeleteDocumentQuery from '@/modules/transaction/queries/useDeleteDocument.query';
import { useMyMoneyNavigation } from '@/modules/UseMyMoney/hooks';
import { useGetEnrollmentsQuery } from '@/modules/UseMyMoney/queries';
import { useDocumentOcrStore, useMyMoneyFormStore, useMyMoneyStore } from '@/modules/UseMyMoney/stores';
import { USE_MY_MONEY_UPLOAD_STORE_KEY, UseMyMoneyStep } from '@/modules/UseMyMoney/UseMyMoney.types';
import AccountsDetailsContainer from '@/modules/UseMyMoney/widgets/AccountDetails/AccountDetails.container';
import UploadFileContainer from '@/modules/UseMyMoney/widgets/UploadFile/UploadFile.container';
import { useDismissBannerQuery, useGetDismissedBannersQuery } from '@/modules/user/queries';
import useEmployeeStore from '@/modules/user/stores/useEmployee.store';

import { SeeWhatsNewButton, SelectFile, UseMyMoneyOptionsList } from './components';

const UseMyMoneyOptionsContainer = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { employee } = useEmployeeStore();
  const { useMyMoneyOption, setUseMyMoneyOption, setReceiptUploadOption } = useMyMoneyStore();
  const { handleReset } = useFilesInDocumentStore(USE_MY_MONEY_UPLOAD_STORE_KEY);
  const { resetDocumentOcrState } = useDocumentOcrStore();
  const { handleResetFormStore } = useMyMoneyFormStore();
  const { handleSetStep } = useMyMoneyNavigation();
  const { organizationProperties } = useOrganizationProperties();
  const { dismissedBanners, isDismissedBannersLoading } = useGetDismissedBannersQuery();
  const { dismissBanner } = useDismissBannerQuery();
  const { deleteDocument } = useDeleteDocumentQuery();

  const { openEnrollments, isEnrollmentsLoading } = useGetEnrollmentsQuery({
    employeeId: employee?.id
  });

  const isHsaWithdrawalAllowed = useMemo(() => {
    if (isEnrollmentsLoading) {
      return false;
    }

    const openHsaEnrollment = openEnrollments?.find((enrollment) => enrollment?.accountType === IAccountDtoAccountTypeEnum.HSA);
    return !!openHsaEnrollment;
  }, [openEnrollments, isEnrollmentsLoading]);

  const isSeeWhatsNewButtonAvailable = useMemo(
    () => !isDismissedBannersLoading && !dismissedBanners.has(DismissedBannerKey.USE_MY_MONEY_SEE_WHATS_NEW),
    [dismissedBanners, isDismissedBannersLoading]
  );

  useBreadcrumbs(
    [
      {
        key: ROUTES.HOME,
        title: t('Home'),
        value: ROUTES.HOME,
      },
      {
        key: ROUTES.USE_MY_MONEY,
        title: t('Use My Money'),
        value: ROUTES.USE_MY_MONEY,
      },
    ],
    true
  );

  const onFileUpload = useCallback(() => {
    handleSetStep(UseMyMoneyStep.READY_TO_SCAN);
  }, [handleSetStep]);

  // Reset all relevant stores when returning to the initial page
  useEffect(() => {
    handleReset();
    resetDocumentOcrState();
    handleResetFormStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box data-testid="use-my-money-options_container">
      <Box data-testid="use-my-money-options_header">
        <Box direction="row" gap="spacing8">
          {isSeeWhatsNewButtonAvailable && <SeeWhatsNewButton dismissBanner={dismissBanner} />}
          {isMobile ? <AccountsDetailsContainer isMobile /> : null}
        </Box>
        <Heading size="regular" data-testid="use-my-money-options_title" margin={{ top: 'spacing12' }}>
          {t('Use My Money')}
        </Heading>
      </Box>

      <Box
        data-testid="use-my-money-options_content"
        margin={{ top: isMobile ? 'spacing16' : 'spacing24' }}
        direction="row"
      >
        <Box flex>
          <UseMyMoneyOptionsList
            selectedOption={useMyMoneyOption}
            setUseMyMoneyOption={setUseMyMoneyOption}
            setReceiptUploadOption={setReceiptUploadOption}
            isReimbursementAllowed={organizationProperties?.isReimbursementAllowed}
            isBillPayAllowed={organizationProperties?.isBillPayAllowed}
            isHsaWithdrawalAllowed={isHsaWithdrawalAllowed}
          />

          {useMyMoneyOption && (
            <Box margin={{ top: 'spacing24' }}>
              <SelectFile
                storeKey={USE_MY_MONEY_UPLOAD_STORE_KEY}
                onUpload={onFileUpload}
                deleteDocument={deleteDocument}
              >
                <UploadFileContainer
                  onUpload={onFileUpload}
                  onQrCodeUpload={onFileUpload}
                  storeKey={USE_MY_MONEY_UPLOAD_STORE_KEY}
                  disableDragAndDrop
                />
              </SelectFile>
            </Box>
          )}
        </Box>

        {isMobile ? null : (
          <Box margin={{ left: 'spacing24' }} width={{ width: '100%', max: '250px' }}>
            <AccountsDetailsContainer />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default UseMyMoneyOptionsContainer;
